import type * as React from "react";
import { memo } from "react";
import PageTitle from "~/common/components/PageTitle/v1/PageTitle";
import UserAccountsTable from "~/features/userAdministration/manageUsers/components/UserAccountsTable";
import UserAccountsPlug from "~/features/userAdministration/manageUsers/components/UserAccountsPlug";
import { useTranslation } from "react-i18next";
import { Button, styled } from "~/common/mui";
import AddIcon from "@mui/icons-material/Add";

export interface UserAccountsComponentProps {
  results: UserAccountEntry[];
  totalAmount: number;
  manageUserLink: string;
  unit: {
    name: string;
    displayUid: string;
  };
}

export interface UserAccountEntry {
  firstName: string;
  lastName: string;
  status: boolean;
  uid: string;
  roles: string[];
}

const UserAccountsTablePage: React.FC<UserAccountsComponentProps> = (props) => {
  const { t } = useTranslation();

  return (
    <>
      <PageTitle title={t("text.company.manageusers.label")} />
      <UnitHeader>
        <UnitName>
          {`${props.unit.name} (${props.unit.displayUid})`}
          {` | `}
          {t("text.company.manageusers.user.count", {
            count: props.results.length,
          })}
        </UnitName>
        <Button
          href={props.manageUserLink}
          data-test-id="create-user-0"
          startIcon={<AddIcon sx={{ fontSize: { phone: 16, tablet: 20 } }} />}
        >
          {t("userList.createNewUser")}
        </Button>
      </UnitHeader>
      <UserAccountsTable {...props} />
      <div className="mt-30 mb-2 d-flex flex-justify-end">
        <Button
          href={props.manageUserLink}
          data-test-id="create-user-1"
          startIcon={<AddIcon sx={{ fontSize: { phone: 16, tablet: 20 } }} />}
        >
          {t("userList.createNewUser")}
        </Button>
      </div>
    </>
  );
};

const UserAccountsPage: React.FC<UserAccountsComponentProps> = (props) => (
  <UserAccountsRoot>
    {props.results.length !== 0 ? (
      <UserAccountsTablePage {...props} />
    ) : (
      <UserAccountsPlug manageUserLink={props.manageUserLink} />
    )}
  </UserAccountsRoot>
);

export default memo(UserAccountsPage);

export interface UserAccountsJSPProps {
  results: UserAccountEntry[];
  totalAmount: number;
  manageUserLink: string;
}

export function createManageUsersPage(json: string) {
  const data: UserAccountsJSPProps = JSON.parse(json);

  return <UserAccountsPage {...data} unit={window.SCHA.user.selectedAccount ?? EMPTY_UNIT} />;
}

const EMPTY_UNIT = { displayUid: "NA", name: "N/A" };

const UserAccountsRoot = styled("div")({
  "& .scha__data-table__cell-row": {
    wordBreak: "break-word",
  },
});

const UnitHeader = styled("div")(({ theme }) => ({
  paddingBottom: theme.spacing(3.75),
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const UnitName = styled("h3")({
  margin: 0,
});
